import FetchUtils from 'src/utils/fetch_utils';
import LayoutUtils from 'src/utils/layout_utils';

/**
 * Standalone time form (time-only inline form) and "Reschedule" / time link.
 * Callbacks: toggleTopMode(popup, mode), refreshDashboardShowing(uid, id), reloadCurrentPopup(popup).
 */
class TimeForm {
  static getTimeFormElements(form) {
    return {
      popup: form.closest('.js-showing-view-popup'),
      errorsWrap: form.querySelector('.js-showing-view-time-errors'),
      selectedInput: form.querySelector('.js-showing-view-time-selected'),
      cancelButton: form.querySelector('.js-showing-view-time-cancel'),
      submitInfo: form.querySelector('.js-showing-view-time-submit-info')
    };
  }

  static setActiveDay(form, dayKey) {
    form.querySelectorAll('.js-showing-view-time-day-toggle').forEach((toggle) => {
      const active = toggle.dataset.dayKey === dayKey;
      toggle.classList.toggle('showing-view-time__date-item--active', active);
      toggle.classList.toggle('js-showing-view-time-day-toggle--active', active);
    });

    form.querySelectorAll('.js-showing-view-time-day-panel').forEach((panel) => {
      LayoutUtils.toggle(panel, panel.dataset.dayKey === dayKey);
    });
  }

  static initDayToggles(form) {
    form.querySelectorAll('.js-showing-view-time-day-toggle').forEach((toggle) => {
      toggle.addEventListener('click', () => {
        TimeForm.setActiveDay(form, toggle.dataset.dayKey);
      });
    });
  }

  static initSlotSelection(form, elements) {
    const { selectedInput, submitInfo } = elements;

    form.querySelectorAll('.js-showing-view-time-slot').forEach((slot) => {
      slot.addEventListener('click', () => {
        form.querySelectorAll('.js-showing-view-time-slot').forEach((s) => {
          s.classList.remove('showing-view-time__slot--selected', 'js-showing-view-time-slot--selected');
        });
        slot.classList.add('showing-view-time__slot--selected', 'js-showing-view-time-slot--selected');
        if (selectedInput) {
          selectedInput.value = slot.dataset.value || '';
        }
        if (submitInfo) {
          submitInfo.textContent = slot.dataset.shortLabel || '';
          LayoutUtils.toggle(submitInfo, !!slot.dataset.shortLabel);
        }
        if (slot.dataset.dayKey) {
          TimeForm.setActiveDay(form, slot.dataset.dayKey);
        }
      });
    });
  }

  static buildTimeFormBody(form) {
    const formData = new FormData(form);
    const body = new URLSearchParams();
    formData.forEach((value, key) => body.append(key, value));
    return body.toString();
  }

  static showTimeErrors(errorsWrap, errors) {
    if (!errorsWrap) return;
    errorsWrap.innerHTML = errors.map((error) => `<div>${error}</div>`).join('');
    LayoutUtils.show(errorsWrap);
  }

  static clearTimeErrors(errorsWrap) {
    if (!errorsWrap) return;
    LayoutUtils.hide(errorsWrap);
    errorsWrap.innerHTML = '';
  }

  static handleTimeSubmitSuccess(response, elements, callbacks) {
    callbacks.refreshDashboardShowing?.(response.uid, response.id);
    document.dispatchEvent(new CustomEvent('showing:updated', { detail: { showingId: `${response.id}` } }));
    callbacks.toggleTopMode?.(elements.popup, 'readonly');
    callbacks.reloadCurrentPopup?.(elements.popup);
  }

  static handleTimeSubmitError(err, errorsWrap) {
    if (err?.payload?.errors) {
      TimeForm.showTimeErrors(errorsWrap, err.payload.errors);
      return;
    }
    if (errorsWrap) {
      TimeForm.showTimeErrors(errorsWrap, ['Unable to update time. Please try again.']);
    }
    FetchUtils.handleResponseError(err);
  }

  static onTimeFormSubmit(form, elements, callbacks) {
    const { errorsWrap, selectedInput } = elements;
    const saveButton = form.querySelector('.js-showing-view-time-submit-btn');

    if (saveButton) saveButton.disabled = true;
    TimeForm.clearTimeErrors(errorsWrap);

    if (!selectedInput?.value) {
      TimeForm.showTimeErrors(errorsWrap, ['Select a time slot.']);
      if (saveButton) saveButton.disabled = false;
      return;
    }

    const updateUrl = form.dataset.updateUrl;
    if (!updateUrl) {
      if (saveButton) saveButton.disabled = false;
      return;
    }

    const body = TimeForm.buildTimeFormBody(form);

    fetch(updateUrl, {
      method: (form.method || 'POST').toUpperCase(),
      headers: {
        'x-csrf-token': FetchUtils.CSRFToken(),
        accept: 'application/json',
        'content-type': 'application/x-www-form-urlencoded; charset=UTF-8'
      },
      body
    })
      .then((resp) => FetchUtils.checkResponseStatus(resp))
      .then((resp) => resp.json())
      .then((response) => {
        if (!response.success) {
          const errors = response.errors || ['Unable to update time.'];
          TimeForm.showTimeErrors(errorsWrap, errors);
          return;
        }
        TimeForm.handleTimeSubmitSuccess(response, elements, callbacks);
      })
      .catch((err) => TimeForm.handleTimeSubmitError(err, errorsWrap))
      .finally(() => {
        if (saveButton) saveButton.disabled = false;
      });
  }

  /**
   * Callbacks: toggleTopMode(popup, mode), refreshDashboardShowing(uid, id), reloadCurrentPopup(popup).
   */
  static initTimeLink(timeLink, callbacks = {}) {
    if (timeLink.dataset.bound === 'true') {
      return;
    }

    timeLink.dataset.bound = 'true';
    timeLink.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      const popup = timeLink.closest('.js-showing-view-popup') || document.querySelector('.js-sm-modal .js-showing-view-popup');
      if (typeof SMDropdown !== 'undefined') {
        SMDropdown.closeAll();
      }
      callbacks.toggleTopMode?.(popup, 'edit');
      popup?.querySelector('.js-showing-view-time-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  /**
   * Callbacks: toggleTopMode(popup, mode), refreshDashboardShowing(uid, id), reloadCurrentPopup(popup).
   */
  static initTimeForm(form, callbacks = {}) {
    if (form.dataset.bound === 'true') {
      return;
    }

    form.dataset.bound = 'true';
    const elements = TimeForm.getTimeFormElements(form);

    elements.cancelButton?.addEventListener('click', () => {
      callbacks.toggleTopMode?.(elements.popup, 'readonly');
    });

    TimeForm.initDayToggles(form);
    TimeForm.initSlotSelection(form, elements);

    form.addEventListener('submit', (e) => {
      e.preventDefault();
      TimeForm.onTimeFormSubmit(form, elements, callbacks);
    });
  }
}

export function initTimeLink(timeLink, callbacks) {
  TimeForm.initTimeLink(timeLink, callbacks);
}

export function initTimeForm(form, callbacks) {
  TimeForm.initTimeForm(form, callbacks);
}
